import { faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox, FormControlLabel } from '@mui/material';
import { formatISO } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Slot } from '../../../../../../../interfaces/interfaces';
import { fetchAndSetSlots } from '../../../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../../../reduxToolkit/typedRedux';
import { createSlot } from '../../../../../../../services/services';
import VyButton from '../../../../../../common/VyButton';
import VyDateTimePicker from '../../../../../../common/VyDateTimePicker';

const NewSlotGroup = ({
  timerange,
  onCreate,
  presentations,
}: {
  timerange?: Slot;
  onCreate: (time: { datetime_start: string; datetime_end: string }, withFetch?: boolean) => void;
  presentations: Slot[];
}) => {
  const [stage, setStage] = useState(0);
  const [skipped, setSkipped] = useState<boolean>(false);
  const { register, handleSubmit } = useForm();
  const event = useSelector(({ common }) => common.event);
  const [dates, setDates] = useState({
    datetime_start: new Date(event?.datetime_start ? event.datetime_start : new Date()),
    datetime_end: new Date(new Date(event?.datetime_end ? event.datetime_end : new Date()).getTime() + 30 * 60 * 1000),
  });

  const { t, i18n } = useTranslation(['slots', 'booth']);
  const [msg, setMsg] = useState('');
  const dispatch = useDispatch();

  async function submit(props: any) {
    setStage(0);
    const dateTimeObject = timerange ? extractDatetimeFromSlot(timerange) : convertDateToDatetimeObject();
    const { datetime_start, datetime_end } = dateTimeObject;

    if (skipped) {
      onCreate({ datetime_start: datetime_start.toString(), datetime_end: datetime_end.toString() });
      return;
    }
    try {
      const bodyProps = { ...props, ...dateTimeObject, sortIndex: 0 };

      await createSlot(bodyProps);
      dispatch(fetchAndSetSlots(event?._id));
    } catch (err) {
      setMsg((err as any)?.response?.data?.translation?.[`${i18n.language}-${i18n.language.toUpperCase()}`] || t('common:error-without-status'));
    }
    if (!event?._id) return;
    onCreate({ datetime_start: datetime_start.toString(), datetime_end: datetime_end.toString() }, true);
  }

  function convertDateToDatetimeObject() {
    return {
      datetime_start: dateToDatetime(dates.datetime_start),
      datetime_end: dateToDatetime(dates.datetime_end),
    };
  }

  const dateToDatetime = (date: Date) => new Date(formatISO(date).slice(0, -6));

  function extractDatetimeFromSlot(slot: Slot) {
    const { datetime_start, datetime_end } = slot;
    return { datetime_start, datetime_end };
  }

  function handleStage() {
    if (stage !== 2) setStage(stage + 1);
  }

  return (
    <>
      <div className="w-full p-2 rounded-md bg-slate-100" style={{ width: '400px' }}>
        <div className="add-new min-h-[180px] flex justify-center items-center p-2 cursor-pointer">
          {stage === 0 && (
            <>
              <div className="flex items-center justify-center w-full h-full" onClick={handleStage} aria-label="add button">
                <FontAwesomeIcon style={{ width: '30px', height: '30px', color: 'var(--vystem-blue)' }} icon={faPlusCircle} />
              </div>
            </>
          )}
          {stage !== 0 && (
            <form onSubmit={handleSubmit(submit)} className="flex flex-col justify-between w-full h-full meetingrooms">
              {stage === 1 && <p className="font-bold">{t('slots:assign-presentation')}</p>}
              <label className="flex flex-col" style={{ display: stage === 1 ? 'inherit' : 'none' }}>
                <small>{t('slots:presentation')}</small>
                <select {...register('meetingroom')} className="p-2 truncate border">
                  {presentations &&
                    presentations.map((p) => (
                      <option key={p.meetingroom._id} value={p.meetingroom._id}>
                        {`${p.meetingroom.contactperson} | ${p.meetingroom.name} | ${p.meetingroom.booth?.name ?? ''}`}
                      </option>
                    ))}
                </select>
              </label>
              <FormControlLabel
                style={{ display: stage === 1 ? 'inherit' : 'none' }}
                control={<Checkbox defaultChecked={false} />}
                {...register('notifyOwners')}
                labelPlacement="end"
                label={t('booth:send-host-invite')}
              />
              <div className="flex flex-col justify-between" style={{ display: stage === 2 && !timerange ? 'inherit' : 'none' }}>
                <label>
                  <small>{t('slots:start-date')}</small>
                  <VyDateTimePicker
                    date={dates?.datetime_start}
                    onChange={(date) => {
                      if (date) {
                        const diff = dates.datetime_end.getTime() - date.getTime();
                        setDates({
                          datetime_end: diff <= 0 ? new Date(date.getTime() + 30 * 60 * 1000) : dates.datetime_end,
                          datetime_start: date || new Date(),
                        });
                      }
                    }}
                  />
                </label>
                <label>
                  <small>{t('slots:end-date')}</small>
                  <VyDateTimePicker date={dates?.datetime_end} onChange={(date) => setDates((dates) => ({ ...dates, datetime_end: date || new Date() }))} />
                </label>
              </div>
              {stage === 1 && !timerange && (
                <div className="flex flex-row justify-between space-x-2">
                  <VyButton className="w-full" onClick={handleStage} disabled={!presentations || presentations.length === 0}>
                    {t('slots:continue')}
                  </VyButton>
                  <VyButton
                    className="w-full"
                    onClick={() => {
                      handleStage();
                      setSkipped(true);
                    }}>
                    {t('slots:only-timeslot')}
                  </VyButton>
                </div>
              )}
              {(timerange || stage === 2) && (
                <VyButton className="mt-2" type="submit">
                  {t('slots:add')}
                </VyButton>
              )}
            </form>
          )}
        </div>
        <p style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>{msg}</p>
      </div>
    </>
  );
};

export default NewSlotGroup;
